import type { Variant } from '../../gameTypes.js';
import {
  JumpPattern,
  tierAtLeast,
  type JumpPatternContext,
  type PatternPlacement,
} from './jumpPatternBase.js';
import { pickClusterCols, rollTallSpike } from './jumpPatternWeights.js';

/** Chance a Double Tap's second hop stands tall (a low→full size switch mid-pair). */
const DOUBLE_TAP_TALL_TWIST = 0.4;

/** Chance a Triple Stutter mixes heights (low/tall/low) rather than three even hops. */
const TRIPLE_MIXED_TWIST = 0.45;

/** Onsets an expert gauntlet claims for its sustained size-switching stream. */
const GAUNTLET_MIN_ANCHORS = 4;
const GAUNTLET_MAX_ANCHORS = 6;

/**
 * P1 — Lone Hop. A single ground spike on one onset, LOW (short hop) or TALL
 * (full jump) by the music. The fundamental beat and the cleanest place to chase
 * the perfect zone. Always available; the workhorse that fills calm verses with
 * gentle low hops (tall is rare outside a kiai).
 */
export class LoneHopPattern extends JumpPattern {
  readonly id = 'lone-hop';
  readonly minTier: Variant = 'normal';
  readonly minAnchors = 1;
  readonly baseWeight = 1.0;

  paint(ctx: JumpPatternContext): PatternPlacement {
    const beat = ctx.anchors[ctx.index].beat;
    if (rollTallSpike(ctx.rng.place(), ctx.inKiai, ctx.progress)) {
      ctx.stamper.tallSpike(beat);
    } else {
      ctx.stamper.lowSpike(beat);
    }
    return { consumed: 1 };
  }
}

/**
 * P2 — Double Tap. Two hops on two consecutive onsets — repeating the same beat,
 * the building block of streams. Twist: the second hop stands tall, forcing a
 * short→full size switch mid-pair.
 */
export class DoubleTapPattern extends JumpPattern {
  readonly id = 'double-tap';
  readonly minTier: Variant = 'normal';
  readonly minAnchors = 2;
  readonly baseWeight = 0.55;

  paint(ctx: JumpPatternContext): PatternPlacement {
    const first = ctx.anchors[ctx.index].beat;
    const second = ctx.anchors[ctx.index + 1].beat;
    ctx.stamper.lowSpike(first);
    const tallSecond = ctx.rng.twist() < DOUBLE_TAP_TALL_TWIST;
    const placedSecond = tallSecond ? ctx.stamper.tallSpike(second) : ctx.stamper.lowSpike(second);
    // The second hop paints nothing if it lands past the last placeable column;
    // only claim the onset we actually stamped so cursor accounting stays honest.
    return { consumed: placedSecond === null ? 1 : 2 };
  }
}

/**
 * P3 — Triple Stutter. The iconic three-in-a-row: tap-tap-tap on three onsets.
 * The middle jump is the classic death; tests cadence stability. Twist
 * (Hard+): low/tall/low mixes a size change into the run.
 */
export class TripleStutterPattern extends JumpPattern {
  readonly id = 'triple-stutter';
  readonly minTier: Variant = 'normal';
  readonly minAnchors = 3;
  readonly baseWeight = 0.45;

  weight(ctx: JumpPatternContext): number {
    // A touch more likely once the chart has warmed up — streams are a mid/late
    // device, not an opener.
    return this.baseWeight * (0.6 + 0.8 * Math.min(1, ctx.progress * 1.5));
  }

  paint(ctx: JumpPatternContext): PatternPlacement {
    const mixed = tierAtLeast(ctx.tier, 'hard') && ctx.rng.twist() < TRIPLE_MIXED_TWIST;
    let consumed = 0;
    for (let i = 0; i < 3; i += 1) {
      const beat = ctx.anchors[ctx.index + i].beat;
      // Mixed twist stands the MIDDLE hop tall (low/tall/low) — the size switch
      // lands on the phrase's centre, the spot players already brace for.
      const placed = mixed && i === 1 ? ctx.stamper.tallSpike(beat) : ctx.stamper.lowSpike(beat);
      // A hop past the last placeable column paints nothing; stop claiming onsets
      // there so `consumed` matches the obstacles actually stamped.
      if (placed === null) {
        break;
      }
      consumed += 1;
    }
    return { consumed };
  }
}

/**
 * P4 — Spike-Wall Commit. One onset becomes a 2–4 wide LOW spike-wall: a single
 * centred full jump, the concave arc clipping an outer column if you're
 * off-centre. Width is rolled from the music and capped by the tier
 * (`maxClusterCols`: doubles on Normal, triples/quads on the fast tiers), and
 * room-gated so it never crowds its neighbours. Falls back to a lone tall spike
 * when the roll/room won't support a wall, so the onset always carries a
 * full-jump obstacle.
 */
export class SpikeWallPattern extends JumpPattern {
  readonly id = 'spike-wall';
  readonly minTier: Variant = 'normal';
  readonly minAnchors = 1;
  readonly baseWeight = 0.5;

  weight(ctx: JumpPatternContext): number {
    // Walls are a drop device — far likelier inside a kiai.
    return this.baseWeight * (ctx.inKiai ? 1.8 : 0.8);
  }

  paint(ctx: JumpPatternContext): PatternPlacement {
    const beat = ctx.anchors[ctx.index].beat;
    const prevEnd = ctx.index > 0 ? ctx.anchors[ctx.index - 1].endBeat : Number.NEGATIVE_INFINITY;
    const nextBeat =
      ctx.index < ctx.anchors.length - 1 ? ctx.anchors[ctx.index + 1].beat : Number.POSITIVE_INFINITY;
    const roomBeats = Math.min(beat - prevEnd, nextBeat - beat);
    const width = pickClusterCols({
      roll: ctx.rng.place(),
      inKiai: ctx.inKiai,
      progress: ctx.progress,
      maxCols: ctx.maxClusterCols,
      roomBeats,
      minGapBeats: ctx.minGapBeats,
      cellBeats: ctx.cellBeats,
    });
    if (width >= 2) {
      ctx.stamper.spikeWall(beat, width);
    } else {
      // No room/roll for a wall — keep the onset a full-jump obstacle as a tall spike.
      ctx.stamper.tallSpike(beat);
    }
    return { consumed: 1 };
  }
}

/**
 * P15 — Spike Gauntlet. The level's climax: a sustained run of alternating
 * low/tall hops across several onsets, demanding precise size-switching at high
 * density. Expert-only and late — the test the warm-up has been teaching.
 */
export class SpikeGauntletPattern extends JumpPattern {
  readonly id = 'spike-gauntlet';
  readonly minTier: Variant = 'extreme';
  readonly minAnchors = GAUNTLET_MIN_ANCHORS;
  readonly baseWeight = 0.35;

  eligible(ctx: JumpPatternContext): boolean {
    // Reserve the gauntlet for the back half so the song builds to it.
    return ctx.progress >= 0.45;
  }

  weight(ctx: JumpPatternContext): number {
    return this.baseWeight * (ctx.inKiai ? 2.2 : 1) * Math.min(1, ctx.progress * 1.3);
  }

  paint(ctx: JumpPatternContext): PatternPlacement {
    const available = Math.min(GAUNTLET_MAX_ANCHORS, ctx.anchors.length - ctx.index);
    const count = GAUNTLET_MIN_ANCHORS + Math.floor(ctx.rng.place() * (available - GAUNTLET_MIN_ANCHORS + 1));
    let consumed = 0;
    for (let i = 0; i < count; i += 1) {
      const beat = ctx.anchors[ctx.index + i].beat;
      // Alternate full/short so every press is a deliberate size choice.
      const placed = i % 2 === 0 ? ctx.stamper.tallSpike(beat) : ctx.stamper.lowSpike(beat);
      // Stop at the first hop that falls past the last placeable column so the
      // claimed onset count matches the spikes actually stamped.
      if (placed === null) {
        break;
      }
      consumed += 1;
    }
    return { consumed };
  }
}
